import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Button,
  Alert,
  CircularProgress,
  Stack,
  Snackbar,
  Card,
  CardContent,
  Divider,
  TextField,
  Switch,
  FormControlLabel,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
} from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { TimePicker } from "@mui/x-date-pickers/TimePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import PhoneIcon from "@mui/icons-material/Phone";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import AddIcon from "@mui/icons-material/Add";
import SaveIcon from "@mui/icons-material/Save";
import servicesService from "../../services/ServicesService";

const TIMEZONE_OPTIONS = [
  { value: "Asia/Kolkata", label: "Asia/Kolkata (IST) - India" },
  { value: "Asia/Dubai", label: "Asia/Dubai (GST) - UAE" },
  { value: "Asia/Singapore", label: "Asia/Singapore (SGT) - Singapore" },
  { value: "America/New_York", label: "America/New_York (EST) - USA" },
  { value: "America/Los_Angeles", label: "America/Los_Angeles (PST) - USA" },
  { value: "Europe/London", label: "Europe/London (GMT) - UK" },
];
const DAYS_OF_WEEK = [
  "MONDAY", "TUESDAY", "WEDNESDAY", "THURSDAY", "FRIDAY", "SATURDAY", "SUNDAY"
];
const DEFAULT_WEEKLY_HOURS = DAYS_OF_WEEK.map(day => ({
  day,
  open: null,
  close: null,
  enabled: false
}));

export default function CallSettings() {
  const [loading, setLoading] = useState(true);
  const [isEnabled, setIsEnabled] = useState(false);
  const [onboardingMode, setOnboardingMode] = useState("");
  const [error, setError] = useState(null);
  const [enabling, setEnabling] = useState(false);
  const [showEditHours, setShowEditHours] = useState(false);
  const [updatingHours, setUpdatingHours] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const [callHoursEnabled, setCallHoursEnabled] = useState(false);
  const [timezone, setTimezone] = useState("Asia/Kolkata");
  const [weeklyHours, setWeeklyHours] = useState(DEFAULT_WEEKLY_HOURS);
  const [holidays, setHolidays] = useState([]);

  const loadSettings = async () => {
    setLoading(true);
    try {
      const whatsapp = await servicesService.getWhatsapp();
      const mode = whatsapp?.getOnboardingMode?.() || "";
      setOnboardingMode(mode);

      const isBusinessApp = mode.toLowerCase() === "business_app";

      if (isBusinessApp) {
        setError("Calling only available for new Api number onboarding clients");
        setLoading(false);
        return;
      }

      if (whatsapp?.phoneNumberId) {
        const settings = await servicesService.getCallingSettings();
        setIsEnabled(settings?.enabled || false);

        if (settings?.call_hours?.weekly_operating_hours) {
          setCallHoursEnabled(true);
          setTimezone(settings.call_hours.timezone_id || "Asia/Kolkata");

          const savedHours = settings.call_hours.weekly_operating_hours;
          const newWeeklyHours = [...weeklyHours];
          savedHours.forEach(saved => {
            const index = newWeeklyHours.findIndex(h => h.day === saved.day_of_week);
            if (index !== -1) {
              newWeeklyHours[index].enabled = true;
              newWeeklyHours[index].open = saved.open_time;
              newWeeklyHours[index].close = saved.close_time;
            }
          });
          setWeeklyHours(newWeeklyHours);

          if (settings.call_hours.holiday_schedule) {
            setHolidays(settings.call_hours.holiday_schedule.map(h => ({ date: h.date })));
          }
        }
      }
      setError(null);
    } catch (err) {
      console.error("Failed to load call settings:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    loadSettings();
  }, []);

  const handleEnable = async () => {
    setEnabling(true);
    try {
      const payload = {
        call_icon_visibility: "DEFAULT",
        callback_permission_status: "ENABLED",
      };

      if (callHoursEnabled) {
        // Get only enabled days with valid times
        const activeWeeklyHours = weeklyHours
          .filter(day => day.enabled && day.open && day.close)
          .map(day => ({
            day_of_week: day.day,
            open_time: day.open,
            close_time: day.close
          }));

        if (activeWeeklyHours.length > 0) {
          payload.call_hours = {
            status: "ENABLED",
            timezone_id: timezone,
            weekly_operating_hours: activeWeeklyHours
          };

          const validHolidays = holidays.filter(h => h.date);
          if (validHolidays.length > 0) {
            payload.call_hours.holiday_schedule = validHolidays.map(h => ({
              date: h.date,
              start_time: "0000",
              end_time: "2359"
            }));
          }
        }
      }

      await servicesService.enableCalling(payload);
      await loadSettings();
      setSnackbar({
        open: true,
        message: callHoursEnabled
          ? "Calling enabled with business hours restriction!"
          : "Calling enabled successfully!",
        severity: "success",
      });
    } catch (err) {
      setSnackbar({
        open: true,
        message: err.message || "Failed to enable calling",
        severity: "error",
      });
    } finally {
      setEnabling(false);
    }
  };

  const handleDisable = async () => {
    if (!window.confirm("Are you sure you want to disable calling?")) return;
    setEnabling(true);
    try {
      await servicesService.disableCalling();
      await loadSettings();
      setSnackbar({
        open: true,
        message: "Calling disabled successfully.",
        severity: "success",
      });
    } catch (err) {
      setSnackbar({
        open: true,
        message: err.message || "Failed to disable calling",
        severity: "error",
      });
    } finally {
      setEnabling(false);
    }
  };
  // Function to update call hours after enable
  const handleUpdateCallHours = async () => {
    setUpdatingHours(true);
    try {
      // Get enabled days with valid times
      const activeWeeklyHours = weeklyHours
        .filter(day => day.enabled && day.open && day.close)
        .map(day => ({
          day_of_week: day.day,
          open_time: day.open,
          close_time: day.close
        }));

      if (activeWeeklyHours.length === 0) {
        setSnackbar({
          open: true,
          message: "Please enable at least one day with valid hours",
          severity: "warning",
        });
        return;
      }

      // Build call hours payload
      const callHours = {
        status: "ENABLED",
        timezone_id: timezone,
        weekly_operating_hours: activeWeeklyHours
      };

      // Add holidays if any
      const validHolidays = holidays.filter(h => h.date);
      if (validHolidays.length > 0) {
        callHours.holiday_schedule = validHolidays.map(h => ({
          date: h.date,
          start_time: "0000",
          end_time: "2359"
        }));
      }
      await servicesService.updateCallHours(callHours);

      setSnackbar({
        open: true,
        message: "Business hours updated successfully!",
        severity: "success",
      });

      setShowEditHours(false);
    } catch (err) {
      setSnackbar({
        open: true,
        message: err.message || "Failed to update business hours",
        severity: "error",
      });
    } finally {
      setUpdatingHours(false);
    }
  };
  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  // Business App Client
  if (onboardingMode === "business_app") {
    return (
      <Box>
        <Alert severity="warning" sx={{ mb: 2 }}>
          <Typography variant="body2" fontWeight={500} gutterBottom>
            Calling Not Available
          </Typography>
          <Typography variant="body2">
            This number was onboarded via WhatsApp Business App. Calling requires New API-number onboarding.
          </Typography>
        </Alert>
      </Box>
    );
  }

  // Fresh Number Client
  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Configure voice calling for your customers. Once enabled, the call icon will appear in WhatsApp chat headers.
      </Typography>

      <Card variant="outlined">
        <CardContent>
          <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
            <PhoneIcon color="primary" />
            <Box>
              <Typography variant="body1" fontWeight={500}>
                WhatsApp Calling
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Allow customers to call your business directly from WhatsApp
              </Typography>
            </Box>
          </Stack>

          <Divider sx={{ my: 2 }} />

          {/* Call Hours Section - Show only when not enabled */}
          {!isEnabled && (
            <>
              <Box sx={{ mt: 2, mb: 2 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={callHoursEnabled}
                      onChange={(e) => setCallHoursEnabled(e.target.checked)}
                    />
                  }
                  label={
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <AccessTimeIcon fontSize="small" color="action" />
                      <Typography variant="body2" fontWeight={500}>
                        Restrict calls to business hours
                      </Typography>
                    </Stack>
                  }
                />
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", ml: 5 }}>
                  When enabled, customers can only call during your business hours.
                </Typography>
              </Box>

              {/* Call Hours Settings Panel */}
              {callHoursEnabled && (
                <Card variant="outlined" sx={{ mb: 2, bgcolor: "#fafafa" }}>
                  <CardContent>
                    <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 2 }}>
                      Business Hours Settings
                    </Typography>

                    {/* Timezone Selection */}
                    <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                      <InputLabel>Timezone</InputLabel>
                      <Select
                        value={timezone}
                        label="Timezone"
                        onChange={(e) => setTimezone(e.target.value)}
                      >
                        {TIMEZONE_OPTIONS.map((option) => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>

                    <Divider sx={{ my: 2 }} />

                    {/* Weekly Operating Hours with Time Picker */}
                    <Typography variant="body2" fontWeight={600} sx={{ mb: 2 }}>
                      Weekly Operating Hours
                    </Typography>

                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      {weeklyHours.map((day, index) => (
                        <Stack key={day.day} direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
                          <Checkbox
                            checked={day.enabled}
                            onChange={(e) => {
                              const newHours = [...weeklyHours];
                              newHours[index].enabled = e.target.checked;
                              setWeeklyHours(newHours);
                            }}
                            size="small"
                          />
                          <Typography sx={{ width: 100, fontSize: 14, fontWeight: 500 }}>
                            {day.day}
                          </Typography>

                          {day.enabled ? (
                            <>
                              <TimePicker
                                label="Open"
                                value={day.open ? dayjs(day.open, "HHmm") : null}
                                onChange={(newValue) => {
                                  const newHours = [...weeklyHours];
                                  newHours[index].open = newValue ? newValue.format("HHmm") : null;
                                  setWeeklyHours(newHours);
                                }}
                                ampm={true}
                                views={["hours", "minutes"]}
                                format="hh:mm a"
                                slotProps={{ textField: { size: "small", sx: { width: 130 } } }}
                              />
                              <Typography variant="body2">to</Typography>
                              <TimePicker
                                label="Close"
                                value={day.close ? dayjs(day.close, "HHmm") : null}
                                onChange={(newValue) => {
                                  const newHours = [...weeklyHours];
                                  newHours[index].close = newValue ? newValue.format("HHmm") : null;
                                  setWeeklyHours(newHours);
                                }}
                                ampm={true}
                                views={["hours", "minutes"]}
                                format="hh:mm a"
                                slotProps={{ textField: { size: "small", sx: { width: 130 } } }}
                              />
                            </>
                          ) : (
                            <Typography variant="caption" color="text.secondary">Closed</Typography>
                          )}
                        </Stack>
                      ))}
                    </LocalizationProvider>

                    <Divider sx={{ my: 2 }} />

                    {/* Holidays Section */}
                    <Typography variant="body2" fontWeight={600} sx={{ mb: 2 }}>
                      Holidays (Optional)
                    </Typography>
                 <Box sx={{ maxHeight: 200, overflow: 'auto', mb: 2 }}>
                    {holidays.map((holiday, index) => (
                      <Stack key={index} direction="row" spacing={2} alignItems="center" sx={{ mb: 1 }}>
                        <TextField
                          size="small"
                          type="date"
                          value={holiday.date}
                          onChange={(e) => {
                            const newHolidays = [...holidays];
                            newHolidays[index].date = e.target.value;
                            setHolidays(newHolidays);
                          }}
                          sx={{ width: 150 }}
                        />
                        <Button
                          size="small"
                          color="error"
                          onClick={() => setHolidays(holidays.filter((_, i) => i !== index))}
                        >
                          Remove
                        </Button>
                      </Stack>
                    ))}
                    </Box>
                    <Button
                      size="small"
                      startIcon={<AddIcon />}
                      onClick={() => {
                        if (holidays.length >= 20) {
                          setSnackbar({
                            open: true,
                            message: "You can only add up to 20 holidays.",
                            severity: "warning",
                          });
                          return; 
                        }
                        setHolidays([...holidays, { date: "" }]);
                      }}
                      sx={{ mt: 1 }}
                    >
                      Add Holiday
                    </Button>

                    
                  </CardContent>
                </Card>
              )}
            </>
          )}
          {isEnabled ? (
            <>
              <Alert severity="success" sx={{ mb: 2 }}>
                <Typography variant="body2">
                  Calling is enabled. The call icon will appear in WhatsApp chats.
                </Typography>
                <Typography variant="caption" sx={{ display: "block", mt: 1 }}>
                  Note: It may take up to 7 days for all WhatsApp clients to reflect the call icon.
                </Typography>
              </Alert>

              {/* Show current business hours if enabled */}
              {callHoursEnabled && (
                <Card variant="outlined" sx={{ mb: 2, bgcolor: "#e8f5e9" }}>
                  <CardContent>
                    <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                      <AccessTimeIcon fontSize="small" color="success" />
                      <Typography variant="body2" fontWeight={600}>
                        Current Business Hours
                      </Typography>
                    </Stack>

                    {weeklyHours.filter(day => day.enabled && day.open && day.close).map(day => (
                      <Typography key={day.day} variant="caption" display="block" sx={{ ml: 4 }}>
                        {day.day}: {dayjs(day.open, "HHmm").format("hh:mm A")} - {dayjs(day.close, "HHmm").format("hh:mm A")}
                      </Typography>
                    ))}
                  </CardContent>
                </Card>
              )}

              {/* Buttons */}
              <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
                <Button
                  variant="outlined"
                  onClick={() => setShowEditHours(!showEditHours)}
                  startIcon={<AccessTimeIcon />}
                >
                  {showEditHours ? "Cancel" : "Edit Business Hours"}
                </Button>
                <Button
                  variant="outlined"
                  color="error"
                  onClick={handleDisable}
                  disabled={enabling}
                >
                  {enabling ? <CircularProgress size={20} /> : "Disable Calling"}
                </Button>
              </Stack>

              {/* Edit Hours Panel */}
              {showEditHours && (
                <Card variant="outlined" sx={{ mt: 2, bgcolor: "#fafafa" }}>
                  <CardContent>
                    <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 2 }}>
                      Edit Business Hours
                    </Typography>

                    {/* Timezone Selection */}
                    <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                      <InputLabel>Timezone</InputLabel>
                      <Select
                        value={timezone}
                        label="Timezone"
                        onChange={(e) => setTimezone(e.target.value)}
                      >
                        {TIMEZONE_OPTIONS.map((option) => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>

                    <Divider sx={{ my: 2 }} />

                    {/* Weekly Operating Hours */}
                    <Typography variant="body2" fontWeight={600} sx={{ mb: 2 }}>
                      Weekly Operating Hours
                    </Typography>

                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      {weeklyHours.map((day, index) => (
                        <Stack key={day.day} direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
                          <Checkbox
                            checked={day.enabled}
                            onChange={(e) => {
                              const newHours = [...weeklyHours];
                              newHours[index].enabled = e.target.checked;
                              setWeeklyHours(newHours);
                            }}
                            size="small"
                          />
                          <Typography sx={{ width: 100, fontSize: 14, fontWeight: 500 }}>
                            {day.day}
                          </Typography>

                          {day.enabled ? (
                            <>
                              <TimePicker
                                label="Open"
                                value={day.open ? dayjs(day.open, "HHmm") : null}
                                onChange={(newValue) => {
                                  const newHours = [...weeklyHours];
                                  newHours[index].open = newValue ? newValue.format("HHmm") : null;
                                  setWeeklyHours(newHours);
                                }}
                                ampm={true}
                                views={["hours", "minutes"]}
                                format="hh:mm a"
                                slotProps={{ textField: { size: "small", sx: { width: 130 } } }}
                              />
                              <Typography variant="body2">to</Typography>
                              <TimePicker
                                label="Close"
                                value={day.close ? dayjs(day.close, "HHmm") : null}
                                onChange={(newValue) => {
                                  const newHours = [...weeklyHours];
                                  newHours[index].close = newValue ? newValue.format("HHmm") : null;
                                  setWeeklyHours(newHours);
                                }}
                                ampm={true}
                                views={["hours", "minutes"]}
                                format="hh:mm a"
                                slotProps={{ textField: { size: "small", sx: { width: 130 } } }}
                              />
                            </>
                          ) : (
                            <Typography variant="caption" color="text.secondary">Closed</Typography>
                          )}
                        </Stack>
                      ))}
                    </LocalizationProvider>

                    <Divider sx={{ my: 2 }} />

                    {/* Holidays Section */}
                    <Typography variant="body2" fontWeight={600} sx={{ mb: 2 }}>
                      Holidays (Optional)
                    </Typography>

                    {holidays.map((holiday, index) => (
                      <Stack key={index} direction="row" spacing={2} alignItems="center" sx={{ mb: 1 }}>
                        <TextField
                          size="small"
                          type="date"
                          value={holiday.date}
                          onChange={(e) => {
                            const newHolidays = [...holidays];
                            newHolidays[index].date = e.target.value;
                            setHolidays(newHolidays);
                          }}
                          sx={{ width: 150 }}
                        />
                        <Button
                          size="small"
                          color="error"
                          onClick={() => setHolidays(holidays.filter((_, i) => i !== index))}
                        >
                          Remove
                        </Button>
                      </Stack>
                    ))}

                    <Button
                      size="small"
                      startIcon={<AddIcon />}
                      onClick={() => setHolidays([...holidays, { date: "" }])}
                      sx={{ mt: 1 }}
                    >
                      Add Holiday
                    </Button>

                    {/* Save Button */}
                    <Stack direction="row" spacing={2} sx={{ mt: 3 }}>
                      <Button
                        variant="contained"
                        onClick={handleUpdateCallHours}
                        disabled={updatingHours}
                        startIcon={updatingHours ? <CircularProgress size={16} /> : <SaveIcon />}
                      >
                        {updatingHours ? "Updating..." : "Save Changes"}
                      </Button>
                      <Button
                        variant="outlined"
                        onClick={() => setShowEditHours(false)}
                      >
                        Cancel
                      </Button>
                    </Stack>
                  </CardContent>
                </Card>
              )}
            </>
          ) : (
            <>
              <Alert severity="info" sx={{ mb: 2 }}>
                <Typography variant="body2">
                  Voice calling is available for new API-number onboarding.
                </Typography>
                <Typography variant="caption" sx={{ display: "block", mt: 0.5 }}>
                  Make sure your messaging limit is 2000+ before enabling.
                </Typography>
              </Alert>
              <Button
                variant="contained"
                color="primary"
                onClick={handleEnable}
                disabled={enabling}
                startIcon={enabling ? <CircularProgress size={16} /> : <PhoneIcon />}
              >
                {enabling ? "Enabling..." : "Enable Calling"}
              </Button>
            </>
          )}
        </CardContent>
      </Card>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}